import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendAxiosRequestWithRetry } from '../services/sendAxiosRequestWithRetry';
import { setNotificationOrders } from '../store/slices/notificationSlice';
import type { AppDispatch, RootState } from '../store';


export default function useOrderNotifications() {
    const dispatch = useDispatch<AppDispatch>();

    const notificationOrders = useSelector((state: RootState) => state.notifications.orders);

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const didFetch = useRef(false);

    const handleNonWaiterCustomerOrderNotifications = async (): Promise<CashBoxOrderSummary[]> => {
        const {
            data: respData,
            // error: respError
        } = await sendAxiosRequestWithRetry<CashBoxOrderSummary[]>({
            method: "get",
            url: "/order/non_waiter_customer_orders",
        });

        if (respData) {
            dispatch(setNotificationOrders(respData));
            return respData;
        }
        // else if (respError) {
        //     // 
        // }

        return [];
    };

    const unlockAudio = () => {
        if (!audioRef.current) return;

        const audio = audioRef.current;
        const unlockPlayPromise = audio.play();
        if (unlockPlayPromise && typeof unlockPlayPromise.then === "function") {
            unlockPlayPromise.then(() => {
                audio.pause();
                audio.currentTime = 0;
            })
            .catch(() => {});
            return;
        }

        audio.pause();
        audio.currentTime = 0;
    };

    // create audio instance
    useEffect(() => {
        audioRef.current = new Audio('/sounds/notification-ding-dong.mp3');
    }, []);

    // unlock audio on first user interaction
    useEffect(() => {
        const unlock = () => {
            unlockAudio();
            document.removeEventListener('pointerdown', unlock);
            document.removeEventListener('touchstart', unlock);
            document.removeEventListener('keydown', unlock);
            document.removeEventListener('click', unlock);
        };

        document.addEventListener('pointerdown', unlock, { passive: true });
        document.addEventListener('touchstart', unlock, { passive: true });
        document.addEventListener('keydown', unlock, { passive: true });
        document.addEventListener('click', unlock);
        return () => {
            document.removeEventListener('pointerdown', unlock);
            document.removeEventListener('touchstart', unlock);
            document.removeEventListener('keydown', unlock);
            document.removeEventListener('click', unlock);
        };
    }, []);

    // fetch once
    useEffect(() => {
        if (didFetch.current) return;
        didFetch.current = true;

        handleNonWaiterCustomerOrderNotifications();
    }, []);

    // play sound every 30s if orders exist
    useEffect(() => {
        if (!notificationOrders.length) return;

        const interval = setInterval(() => {
            const playPromise = audioRef.current?.play();
            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => {});
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [notificationOrders]);

    return {
        notificationOrders,
        refetchOrderNotifications: handleNonWaiterCustomerOrderNotifications,
    };
}
